import type { OpportunityDeal } from '~/hooks/apis/offers/useGetOffer';
import type {
  OpportunityRelatedDeal,
  OpportunityRelatedExtraGuestSurcharge,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import { formatDateShortDD, parseDayjs, rangeOverlap } from '~/services/TimeService';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import { toExclusiveEnd } from '~/utils/surchargeBoundaries';

import type { SetupRuleInput } from './setupRules';

type RecordLike = Record<string, unknown>;

export type ChecklistStatus = 'approved' | 'needsApproval' | 'requires approval' | 'blocked';
export type MarginBand = 'red' | 'yellow' | 'green';

export interface EgsMarginChecklistRow {
  ageRange?: string;
  beddingType?: string;
  collectedBy?: string;
  id: string;
  inclusions?: string;
  costPrice?: number;
  currencyCode: string;
  extraGuestType?: string;
  field: 'Extra Guest Surcharges margin';
  margin?: number;
  name: string;
  opportunityName?: string;
  sellPrice?: number;
  status: ChecklistStatus;
  statusLabel: string;
  marginBand?: MarginBand;
}

export interface EgsCapacityChecklistRow {
  egsCapacity?: number;
  field: 'EGS capacity';
  id: string;
  includedCapacity?: number;
  maxRoomCapacity?: number;
  name: string;
  roomRateId?: string;
  roomName: string;
  roomTypeId?: string;
  status: ChecklistStatus;
  statusLabel: string;
}

export type CampaignChecklistDataPoint = {
  label: string;
  value: string;
};

export interface CampaignChecklistRow {
  action?: string;
  approvalNote?: string;
  dataPoints: Array<CampaignChecklistDataPoint>;
  evidence: string;
  field:
    | 'Date change allowed'
    | 'Days prior to check-in for date changes'
    | 'Number of date changes'
    | 'Booking data cut-off'
    | 'Package upgrades allowed'
    | 'SEM support'
    | 'Campaign type'
    | 'Payment Terms Modification'
    | 'Contract Terms Modification'
    | 'Book By date'
    | 'Suggested value';
  id: string;
  packageName?: string;
  rule: string;
  source: string;
  status: ChecklistStatus;
  statusLabel: string;
  summary: string;
  value: string;
}

export interface DatesRestrictionsChecklistRow {
  details: Array<{ label: string; value: string }>;
  field: 'Flexi night + deal options' | 'Offer discounts' | 'Flexi night margin';
  id: string;
  rule: string;
  sourceValue: string;
  status: ChecklistStatus;
  statusLabel: string;
  summary: string;
  title: string;
}

export interface BundledExperienceChecklistRow {
  blackoutDateRange?: string;
  blackoutsOk: boolean;
  bundledExperience: string;
  childAge?: string;
  childAgeOk: boolean;
  currency?: string;
  currencyOk: boolean;
  dealCurrency?: string;
  field: 'Bundled Experiences';
  id: string;
  linkedDealOptions: Array<string>;
  status: ChecklistStatus;
  statusLabel: string;
  travelFrom?: string;
  travelTo?: string;
  validityOk: boolean;
}

export const EGS_MARGIN_FIELD = 'Extra Guest Surcharges margin';
export const EGS_CAPACITY_FIELD = 'EGS capacity';
export const BUNDLED_EXPERIENCES_FIELD = 'Bundled Experiences';
export const DATES_RESTRICTIONS_FIELDS = {
  flexiDealOptions: 'Flexi night + deal options',
  flexiNightMargin: 'Flexi night margin',
  offerDiscounts: 'Offer discounts',
} as const;

export interface BlackoutChecklistRow {
  festiveClear: boolean;
  field: 'Blackout Dates';
  from?: string;
  id: string;
  name: string;
  status: ChecklistStatus;
  statusLabel: string;
  to?: string;
  withinTravelValidity: boolean;
}

export interface BlackoutCoverageSummary {
  blackoutDays: number;
  percent: number;
  status: ChecklistStatus;
  travelDays?: number;
}

type BlackoutDateInput = App.OpportunityPage['black_out_dates'][number] & {
  sfid?: string;
};

export const BLACKOUT_DATES_FIELD = 'Blackout Dates';
export const FESTIVE_DATE_LABEL = 'Dec 24, Dec 25, Dec 31';

const DAY_MS = 24 * 60 * 60 * 1000;

const FLASH_CAMPAIGN_TYPES = ['FLASH - flexi nights', 'FLASH - standard'];
const MAX_BOOK_BY_DAYS_BEFORE_TRAVEL_TO = 62;
const MIN_BOOK_BY_DAYS_BEFORE_TRAVEL_TO = 25;

type PolicyBibleCampaignRule = {
  chain: string;
  dateChangeAllowedDays?: number;
  geo: string;
  numberOfDateChanges?: string;
};

const POLICY_BIBLE_CAMPAIGN_RULES: Array<PolicyBibleCampaignRule> = [
  { chain: 'Accor', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Accor', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Ascott', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Ascott', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Ayana', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Clermont', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'EVT', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'EVT', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Grecotel', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Highgate', geo: 'Intl', dateChangeAllowedDays: 30, numberOfDateChanges: '1' },
  { chain: 'Hyatt', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Hyatt', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'IHG', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'IHG', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Kimpton', geo: 'Intl', dateChangeAllowedDays: 30, numberOfDateChanges: '1' },
  { chain: 'Langham', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Langham', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'Marriott', geo: 'Intl/Aus', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Mulia', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Oberoi', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Oberoi', geo: 'India', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Pro Invest', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Pro Invest', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'India', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'International', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard', geo: 'USA', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  { chain: 'Standard (GOLF wholesale)', geo: 'Intl', dateChangeAllowedDays: 21, numberOfDateChanges: 'Unlimited' },
  {
    chain: 'Standard (GOLF WHOLESALE)',
    geo: 'Australia',
    dateChangeAllowedDays: 21,
    numberOfDateChanges: 'Unlimited',
  },
  { chain: 'TFE', geo: 'Intl', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'TFE', geo: 'Australia', dateChangeAllowedDays: 45, numberOfDateChanges: '1' },
  { chain: 'The Star', geo: 'Australia', dateChangeAllowedDays: 21, numberOfDateChanges: '1' },
];

const EXTRA_GUEST_CAPACITY_FIELDS = [
  'extra_guest_capacity',
  'extra_guest_cap',
  'extra_guest_count',
  'extra_guests',
  'max_extra_guests',
  'max_extra_guest_capacity',
  'egs_capacity',
];

const INCLUDED_CAPACITY_FIELDS = [
  'included_capacity',
  'included_guests',
  'max_included_guests',
  'max_guests_included',
  'number_of_guests',
];

const MAX_ROOM_CAPACITY_FIELDS = ['max_capacity', 'max_occupancy', 'capacity', 'max_guests'];
const EXTRA_NIGHT_MARGIN_FIELDS = ['extra_night_margin', 'extra_night_margin__c', 'Extra_Night_Margin__c'];

export function calculateMarginPercentage(costPrice?: number, sellPrice?: number) {
  if (!isPositiveNumber(sellPrice) || !isFiniteNumber(costPrice)) {
    return undefined;
  }

  return ((sellPrice - costPrice) / sellPrice) * 100;
}

export function getMarginBand(margin?: number): MarginBand | undefined {
  if (!isFiniteNumber(margin)) {
    return undefined;
  }

  if (margin <= 10) {
    return 'red';
  }

  if (margin >= 19) {
    return 'green';
  }

  return 'yellow';
}

export function getMarginStatus(marginBand?: MarginBand): ChecklistStatus {
  if (!marginBand) {
    return 'requires approval';
  }

  return marginBand === 'green' ? 'approved' : 'requires approval';
}

export function buildEgsMarginRows(
  surcharges: Array<SalesforceSurchargePeriod>,
  relatedExtraGuestSurcharges: Array<OpportunityRelatedExtraGuestSurcharge> = [],
): Array<EgsMarginChecklistRow> {
  const relatedSurchargesById = new Map(
    relatedExtraGuestSurcharges.map((surcharge) => [String(surcharge.id), surcharge]),
  );

  return surcharges.map((surcharge) => {
    const relatedSurcharge =
      relatedSurchargesById.get(String(surcharge.sfid)) ?? relatedSurchargesById.get(String(surcharge.id));
    const costPrice = isFiniteNumber(surcharge.converted_cost) ? surcharge.converted_cost : surcharge.cost_price;
    const sellPrice = surcharge.sell_price;
    const margin = calculateMarginPercentage(costPrice, sellPrice);
    const marginBand = getMarginBand(margin);
    const status = getMarginStatus(marginBand);

    return {
      ageRange: normalizeString(relatedSurcharge?.age_range__c),
      beddingType: normalizeString(relatedSurcharge?.other_extra_bedding_type__c || relatedSurcharge?.bedding_type__c),
      collectedBy: normalizeString(relatedSurcharge?.collected_by__c) ?? surcharge.collected_by,
      id: String(surcharge.id),
      costPrice,
      currencyCode: surcharge.currency_code || 'AUD',
      extraGuestType: normalizeString(relatedSurcharge?.extra_guest_type__c),
      field: EGS_MARGIN_FIELD,
      inclusions: normalizeString(relatedSurcharge?.inclusions__c),
      margin,
      marginBand,
      name:
        normalizeString(relatedSurcharge?.name) ?? surcharge.name ?? surcharge.opportunity_name ?? String(surcharge.id),
      opportunityName: surcharge.opportunity_name,
      sellPrice,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

export function buildEgsCapacityRows({
  packages,
  propertyRoomTypes,
  surcharges,
}: {
  packages?: Array<App.Package>;
  propertyRoomTypes?: Array<App.RoomType>;
  surcharges: Array<SalesforceSurchargePeriod>;
}): Array<EgsCapacityChecklistRow> {
  const surchargesEgsCapacity = getMaxDefinedNumber(
    surcharges.map((surcharge) => readNumber(surcharge as unknown as RecordLike, EXTRA_GUEST_CAPACITY_FIELDS)),
  );

  return (packages ?? []).map((offerPackage, index) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const roomTypeId = readString(packageRecord, ['fk_room_type_id']);
    const roomRateId = readString(packageRecord, ['fk_room_rate_id']);
    const roomType = propertyRoomTypes?.find((room) => String(room.id) === roomTypeId);
    const roomRate = findRoomRate(roomType, roomRateId);
    const includedCapacity =
      readNumber(packageRecord, INCLUDED_CAPACITY_FIELDS) ??
      readIncludedGuestsCapacity(roomRate) ??
      readNumber((roomType ?? {}) as RecordLike, ['max_included_guests']);
    const egsCapacity = readNumber(packageRecord, EXTRA_GUEST_CAPACITY_FIELDS) ?? surchargesEgsCapacity;
    const maxRoomCapacity = getMaxRoomCapacity(roomType);
    const status =
      isFiniteNumber(includedCapacity) && isFiniteNumber(egsCapacity) && isFiniteNumber(maxRoomCapacity)
        ? maxRoomCapacity === includedCapacity + egsCapacity
          ? 'approved'
          : 'requires approval'
        : 'requires approval';

    return {
      egsCapacity,
      field: EGS_CAPACITY_FIELD,
      id: `${String(offerPackage.id_salesforce_external ?? offerPackage.le_package_id ?? index)}:${roomTypeId ?? ''}:${
        roomRateId ?? ''
      }`,
      includedCapacity,
      maxRoomCapacity,
      name: offerPackage.deal_option_name || offerPackage.name || `Package ${index + 1}`,
      roomRateId,
      roomName: roomType?.name || 'Room not mapped',
      roomTypeId,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

export function buildBlackoutRows({
  blackOutDates,
  hasSourceError,
  travelFrom,
  travelTo,
}: {
  blackOutDates?: Array<BlackoutDateInput>;
  hasSourceError?: boolean;
  travelFrom?: string;
  travelTo?: string;
}): Array<BlackoutChecklistRow> {
  if (hasSourceError) {
    return [
      evaluateBlackoutRow({
        id: 'blackout-source-unavailable',
        name: 'Opportunity blackout dates unavailable',
        travelFrom,
        travelTo,
      }),
    ];
  }

  return (blackOutDates ?? []).map((blackoutDate, index) =>
    evaluateBlackoutRow({
      from: blackoutDate.from__c,
      id: getBlackoutRowId(blackoutDate, index),
      name: blackoutDate.name || `Blackout ${index + 1}`,
      to: blackoutDate.to__c,
      travelFrom,
      travelTo,
    }),
  );
}

function getBlackoutRowId(blackoutDate: BlackoutDateInput, index: number) {
  if (blackoutDate.sfid) {
    return blackoutDate.sfid;
  }

  return blackoutDate.opportunity__c
    ? `${blackoutDate.opportunity__c}:${blackoutDate.name || index}:${blackoutDate.from__c || ''}:${
        blackoutDate.to__c || ''
      }`
    : `blackout:${index}`;
}

export function evaluateBlackoutRow({
  from,
  id,
  name,
  to,
  travelFrom,
  travelTo,
}: {
  from?: string;
  id: string;
  name: string;
  to?: string;
  travelFrom?: string;
  travelTo?: string;
}): BlackoutChecklistRow {
  const blackoutRange = parseDateRange(from, to);
  const travelRange = parseDateRange(travelFrom, travelTo);
  const withinTravelValidity =
    !!blackoutRange &&
    !!travelRange &&
    blackoutRange.start >= travelRange.start &&
    blackoutRange.end <= travelRange.end;
  const festiveClear = !!blackoutRange && !rangeOverlapsFestiveDates(blackoutRange);
  const status: ChecklistStatus = withinTravelValidity && festiveClear ? 'approved' : 'requires approval';

  return {
    festiveClear,
    field: BLACKOUT_DATES_FIELD,
    from,
    id,
    name,
    status,
    statusLabel: getStatusLabel(status),
    to,
    withinTravelValidity,
  };
}

export function calculateBlackoutCoverage({
  blackOutDates,
  travelFrom,
  travelTo,
}: {
  blackOutDates: Array<Pick<BlackoutChecklistRow, 'from' | 'to'>>;
  travelFrom?: string;
  travelTo?: string;
}): BlackoutCoverageSummary {
  const travelRange = parseDateRange(travelFrom, travelTo);
  if (!travelRange) {
    return {
      blackoutDays: 0,
      percent: 0,
      status: 'requires approval',
    };
  }

  const ranges = blackOutDates
    .map((blackoutDate) => parseDateRange(blackoutDate.from, blackoutDate.to))
    .filter((range): range is DateRange => !!range)
    .map((range) => ({
      end: Math.min(range.end, travelRange.end),
      start: Math.max(range.start, travelRange.start),
    }))
    .filter((range) => range.start <= range.end)
    .sort((a, b) => a.start - b.start);
  const mergedRanges = mergeDateRanges(ranges);
  const blackoutDays = mergedRanges.reduce((sum, range) => sum + inclusiveDays(range), 0);
  const travelDays = inclusiveDays(travelRange);
  const percent = travelDays > 0 ? Math.round((blackoutDays / travelDays) * 100) : 0;

  return {
    blackoutDays,
    percent,
    status: percent < 75 ? 'approved' : 'requires approval',
    travelDays,
  };
}

export type TaxesPaymentsField =
  | 'City/Other Tax Payable Direct'
  | 'Per-room city taxes in Admin Portal'
  | 'Payment mechanism and milestone rules'
  | 'Prize / Prize Used';

export interface TaxesPaymentsChecklistRow {
  details: Array<{ label: string; value: string }>;
  field: TaxesPaymentsField;
  id: string;
  name: string;
  rule: string;
  status: ChecklistStatus;
  statusLabel: string;
}

export function buildTaxesPaymentsRows({
  cityOtherTax,
  cityTaxComments,
  hasPropertyTaxesPayableAtProperty,
  opportunityDataAvailable,
  paymentMethod,
  paymentMilestone,
  prizeOrderId,
  propertyDataAvailable,
}: {
  cityOtherTax?: boolean;
  cityTaxComments?: string;
  hasPropertyTaxesPayableAtProperty: boolean;
  opportunityDataAvailable: boolean;
  paymentMethod?: string;
  paymentMilestone?: string;
  prizeOrderId?: string;
  propertyDataAvailable: boolean;
}): Array<TaxesPaymentsChecklistRow> {
  const cityTaxStatus = buildCityTaxStatus({ cityOtherTax, cityTaxComments, opportunityDataAvailable });
  const perRoomTaxStatus = buildPerRoomTaxStatus({
    cityOtherTax,
    hasPropertyTaxesPayableAtProperty,
    opportunityDataAvailable,
    propertyDataAvailable,
  });
  const paymentStatus = buildPaymentMechanismStatus({ opportunityDataAvailable, paymentMethod, paymentMilestone });
  const prizeStatus = buildPrizeStatus({ opportunityDataAvailable, prizeOrderId });

  return [
    {
      details: [
        {
          label: 'City/Other Tax Payable Direct',
          value: !opportunityDataAvailable ? 'N/A' : cityOtherTax ? 'Yes' : 'No',
        },
        { label: 'Details', value: cityTaxComments?.trim() || 'Not provided' },
      ],
      field: 'City/Other Tax Payable Direct',
      id: 'city-tax-payable-direct',
      name: 'City/Other Tax Payable Direct',
      rule: 'If City/Other Tax Payable Direct is marked Yes, the tax details field must be populated before approval.',
      status: cityTaxStatus,
      statusLabel: getStatusLabel(cityTaxStatus),
    },
    {
      details: [
        {
          label: 'Per-room city taxes payable at property',
          value: !propertyDataAvailable ? 'N/A' : hasPropertyTaxesPayableAtProperty ? 'Loaded' : 'Not loaded',
        },
      ],
      field: 'Per-room city taxes in Admin Portal',
      id: 'per-room-city-taxes',
      name: 'Per-room city taxes in Admin Portal',
      rule: 'If per-room city taxes are payable directly to the property, they must be loaded in the Admin Portal before the offer goes live.',
      status: perRoomTaxStatus,
      statusLabel: getStatusLabel(perRoomTaxStatus),
    },
    {
      details: [
        { label: 'Payment method', value: paymentMethod?.trim() || 'Not provided' },
        { label: 'Milestone rules', value: paymentMilestone?.trim() || 'Not provided' },
      ],
      field: 'Payment mechanism and milestone rules',
      id: 'payment-mechanism-milestone',
      name: 'Payment mechanism and milestone rules',
      rule: 'Both payment method and milestone rules must be populated, and milestone percentages must sum to 100%.',
      status: paymentStatus,
      statusLabel: getStatusLabel(paymentStatus),
    },
    {
      details: [{ label: 'Prize order ID', value: prizeOrderId?.trim() || 'None' }],
      field: 'Prize / Prize Used',
      id: 'prize-used',
      name: 'Prize / Prize Used',
      rule: 'If a prize order ID is present, the offer requires CM team approval before going live.',
      status: prizeStatus,
      statusLabel: getStatusLabel(prizeStatus),
    },
  ];
}

export function buildCampaignRows({
  offer,
  opportunity,
  opportunityDeals = [],
  setupInput,
}: {
  offer?: App.Offer;
  opportunity?: App.OpportunityPage;
  opportunityDeals?: Array<OpportunityDeal>;
  setupInput?: SetupRuleInput;
}): Array<CampaignChecklistRow> {
  const offerRecord = (offer ?? {}) as unknown as RecordLike;
  const functionalDetails = (opportunity?.functional_details ?? {}) as unknown as RecordLike;
  const keyDates = (opportunity?.key_dates ?? {}) as unknown as RecordLike;
  const keyCommercial = (opportunity?.key_commercial ?? {}) as unknown as RecordLike;
  const contract = (opportunity?.contract ?? {}) as unknown as RecordLike;
  const opportunityInfo = (opportunity?.opportunity_info ?? {}) as unknown as RecordLike;

  const dateChangeAllowed = readBoolean(functionalDetails, ['date_change_allowed__c']);
  const daysBeforeCheckIn =
    readNumber(functionalDetails, ['days_before_check_in_changes_disallowed__c']) ??
    readNumber(offerRecord, ['days_before_check_in_changes_disallowed']);
  const numberOfDateChanges =
    readString(functionalDetails, ['number_of_date_changes__c']) ?? offer?.number_of_date_changes;
  const bookingCutoff =
    readNumber(functionalDetails, ['booking_data_cut_off_days__c']) ??
    readNumber(offerRecord, ['booking_min_lead_days']);
  const packageUpgradesAllowed =
    readBoolean(functionalDetails, ['package_upgrades_allowed__c']) ?? offer?.package_upgrades_allowed;
  const semSupport = deriveSemSupport(
    readBoolean(keyCommercial, ['no_sem_support_for_this_deal__c']) ?? readBoolean(offerRecord, ['sf_no_sem_support']),
  );
  const campaignType = readString(opportunityInfo, ['campaign_type__c']) ?? offer?.campaign_type ?? offer?.type;
  const paymentTermsModification =
    readString(contract, ['payment_terms_modification__c']) ??
    readString(contract, ['partner_b_payment_terms_modification__c']) ??
    readString(offerRecord, ['special_payment_terms']);
  const contractTermsModification = readString(contract, ['partner_b_contract_terms_modification__c']);
  const bookByDate = readString(keyDates, ['book_by__c']) ?? offer?.book_by_date;
  const travelToDate = readString(keyDates, ['travel_to__c']) ?? offer?.travel_to_date;
  const policyBibleRule = findPolicyBibleCampaignRule(setupInput);

  return [
    buildPolicyBibleNumberRow({
      field: 'Date change allowed',
      id: 'campaign-date-change-allowed',
      loadedValue: dateChangeAllowed ? daysBeforeCheckIn : undefined,
      policyBibleRule,
      source: 'Opportunity functional details: date_change_allowed__c',
      value: dateChangeAllowed ? formatOptionalNumber(daysBeforeCheckIn) : formatBooleanValue(dateChangeAllowed),
    }),
    buildPolicyBibleNumberRow({
      field: 'Days prior to check-in for date changes',
      id: 'campaign-date-change-prior-days',
      loadedValue: daysBeforeCheckIn,
      policyBibleRule,
      source: 'Opportunity functional details: days_before_check_in_changes_disallowed__c',
      value: formatOptionalNumber(daysBeforeCheckIn),
    }),
    buildPolicyBibleTextRow({
      field: 'Number of date changes',
      id: 'campaign-number-date-changes',
      loadedValue: numberOfDateChanges,
      policyBibleRule,
      source: 'Opportunity functional details: number_of_date_changes__c',
      value: formatStringValue(numberOfDateChanges),
    }),
    buildCampaignRow({
      action: bookingCutoff == null ? 'Populate booking data cut-off with 2.' : undefined,
      approvalNote: bookingCutoff == null ? 'Booking data cut-off needs to be populated before approval.' : undefined,
      approved: bookingCutoff != null,
      dataPoints: [
        { label: 'Loaded value', value: bookingCutoff == null ? 'Blank' : String(bookingCutoff) },
        { label: 'Expected value', value: '2' },
      ],
      evidence:
        bookingCutoff == null
          ? 'Booking data cut-off is blank and should be populated to 2.'
          : 'Booking data cut-off is populated.',
      field: 'Booking data cut-off',
      id: 'campaign-booking-cutoff',
      rule: 'If booking data cut-off is blank, populate = 2.',
      source: 'Opportunity functional details / offer booking_min_lead_days',
      value: bookingCutoff == null ? 'Blank' : String(bookingCutoff),
    }),
    buildCampaignRow({
      approved: packageUpgradesAllowed,
      dataPoints: [
        { label: 'Loaded value', value: formatBooleanValue(packageUpgradesAllowed) },
        { label: 'Expected value', value: 'Yes' },
      ],
      evidence: packageUpgradesAllowed
        ? 'Package upgrades are allowed.'
        : 'Package upgrades are not allowed or source value is missing.',
      field: 'Package upgrades allowed',
      id: 'campaign-package-upgrades',
      rule: 'Flag for approval when package upgrades are not allowed.',
      source: 'Opportunity functional details / offer package_upgrades_allowed',
      value: formatBooleanValue(packageUpgradesAllowed),
    }),
    buildCampaignRow({
      approved: semSupport !== 'Yes',
      dataPoints: [
        { label: 'Loaded value', value: semSupport },
        { label: 'Expected value', value: 'No' },
      ],
      evidence: semSupport === 'Yes' ? 'SEM support is enabled.' : 'SEM support is not enabled.',
      field: 'SEM support',
      id: 'campaign-sem-support',
      rule: 'Flag for approval only when SEM support is Yes.',
      source: 'Opportunity key commercial no_sem_support_for_this_deal__c / offer sf_no_sem_support',
      value: semSupport,
    }),
    buildCampaignRow({
      action:
        campaignType === 'FLASH - standard'
          ? 'Add note that flexi nights will not pull through to contract.'
          : undefined,
      approvalNote: campaignType === 'FLASH - standard' ? 'Flexi nights will not pull through to contract.' : undefined,
      approved: false,
      dataPoints: [
        { label: 'Loaded value', value: formatStringValue(campaignType) },
        { label: 'Expected value', value: 'FLASH - flexi nights or FLASH - standard' },
      ],
      evidence: FLASH_CAMPAIGN_TYPES.includes(campaignType || '')
        ? 'Campaign type is one of the expected Flash values.'
        : 'Campaign type is missing or outside the expected Flash values.',
      field: 'Campaign type',
      id: 'campaign-type',
      rule: 'Always flag campaign type for approval.',
      source: 'Opportunity info campaign_type__c / offer campaign_type',
      value: formatStringValue(campaignType),
    }),
    buildCampaignRow({
      approved: !normalizeString(String(paymentTermsModification ?? '')),
      dataPoints: [
        { label: 'Loaded value', value: formatStringValue(paymentTermsModification) },
        { label: 'Expected value', value: 'Blank' },
      ],
      evidence: paymentTermsModification
        ? 'Payment terms modification is populated.'
        : 'Payment terms modification is blank.',
      field: 'Payment Terms Modification',
      id: 'campaign-payment-terms-modification',
      rule: 'Auto-approve when the field is blank.',
      source: 'Opportunity contract payment terms modification fields',
      value: formatStringValue(paymentTermsModification),
    }),
    buildCampaignRow({
      approved: !normalizeString(contractTermsModification),
      dataPoints: [
        { label: 'Loaded value', value: formatStringValue(contractTermsModification) },
        { label: 'Expected value', value: 'Blank' },
      ],
      evidence: contractTermsModification
        ? 'Contract terms modification is populated.'
        : 'Contract terms modification is blank.',
      field: 'Contract Terms Modification',
      id: 'campaign-contract-terms-modification',
      rule: 'Auto-approve when the field is blank.',
      source: 'Opportunity contract partner_b_contract_terms_modification__c',
      value: formatStringValue(contractTermsModification),
    }),
    buildBookByRow({ bookByDate, travelToDate }),
    ...buildSuggestedValueRows(offer?.packages, opportunityDeals),
  ];
}

export type SurchargesField = 'Surcharge Coverage' | 'Overlapping Surcharge Dates';

export interface SurchargesChecklistRow {
  details: Array<{ label: string; value: string }>;
  field: SurchargesField;
  id: string;
  name: string;
  status: ChecklistStatus;
  statusLabel: string;
}

export interface SurchargeTableRow {
  accumulate: boolean;
  blackoutOverlap: boolean | null;
  convertedCost?: number;
  currencyCode: string;
  dealOptionNames: Array<string>;
  fromDate: string;
  id: string;
  insideTravelPeriod: boolean | null;
  name: string;
  needsReview: boolean;
  status: ChecklistStatus;
  statusLabel: string;
  surchargePrice?: number;
  toDate: string;
}

export type BlackoutDateRange = { name: string; from__c: string; to__c: string };

export function buildSurchargesRows({
  surcharges,
  travelFromDate,
  travelToDate,
}: {
  surcharges: Array<SalesforceSurchargePeriod>;
  travelFromDate?: string;
  travelToDate?: string;
}): Array<SurchargesChecklistRow> {
  // Rule 1: Surcharge coverage
  const travelPeriodData =
    travelFromDate && travelToDate ? buildTravelPeriodData(travelFromDate, travelToDate, surcharges) : null;
  const travelDays = travelPeriodData?.travelDays ?? 0;
  const surchargeDays = travelPeriodData?.surchargeDays ?? 0;
  const coveragePercent = travelPeriodData && travelDays > 0 ? (surchargeDays / travelDays) * 100 : undefined;
  const coverageStatus: ChecklistStatus = !travelPeriodData
    ? 'blocked'
    : coveragePercent !== undefined && coveragePercent > 75
      ? 'needsApproval'
      : 'approved';

  // Rule 2: Overlapping surcharge date ranges
  const overlappingPairs: Array<string> = [];
  for (let i = 0; i < surcharges.length; i++) {
    for (let j = i + 1; j < surcharges.length; j++) {
      const a = surcharges[i];
      const b = surcharges[j];
      if (surchargesOverlapDates(a.start_date, a.end_date, b.start_date, b.end_date)) {
        overlappingPairs.push(`${a.name} & ${b.name}`);
      }
    }
  }
  const overlapStatus: ChecklistStatus = overlappingPairs.length > 0 ? 'needsApproval' : 'approved';

  return [
    {
      details: [
        { label: 'Travel period days', value: travelPeriodData ? String(travelDays) : 'N/A' },
        { label: 'Surcharge days covered', value: travelPeriodData ? String(surchargeDays) : 'N/A' },
        { label: 'Coverage', value: coveragePercent != null ? `${coveragePercent.toFixed(1)}%` : 'N/A' },
        { label: 'Travel from', value: travelFromDate ? formatContextDate(travelFromDate) : 'Not available' },
        { label: 'Travel to', value: travelToDate ? formatContextDate(travelToDate) : 'Not available' },
      ],
      field: 'Surcharge Coverage',
      id: 'surcharges-coverage',
      name: 'Surcharge coverage rule',
      status: coverageStatus,
      statusLabel: getStatusLabel(coverageStatus),
    },
    {
      details: [{ label: 'Overlapping pairs', value: overlappingPairs.join('; ') || 'None' }],
      field: 'Overlapping Surcharge Dates',
      id: 'surcharges-overlapping-dates',
      name: 'Overlapping surcharge dates',
      status: overlapStatus,
      statusLabel: getStatusLabel(overlapStatus),
    },
  ];
}

export function buildSurchargeTableRows({
  blackoutDates,
  packages,
  surcharges,
  travelFromDate,
  travelToDate,
}: {
  blackoutDates?: Array<BlackoutDateRange>;
  packages?: Array<App.Package>;
  surcharges: Array<SalesforceSurchargePeriod>;
  travelFromDate?: string;
  travelToDate?: string;
}): Array<SurchargeTableRow> {
  const dealOptionNames = (packages ?? [])
    .map((offerPackage) => offerPackage.deal_option_name || offerPackage.name || '')
    .filter(Boolean);

  return surcharges.map((surcharge) => {
    const insideTravelPeriod =
      travelFromDate && travelToDate
        ? surcharge.start_date >= travelFromDate && surcharge.end_date <= travelToDate
        : null;

    const blackoutOverlap = blackoutDates
      ? blackoutDates.some((blackoutDate) =>
          surchargesOverlapDates(surcharge.start_date, surcharge.end_date, blackoutDate.from__c, blackoutDate.to__c),
        )
      : null;

    const needsReview =
      surcharge.accumulate ||
      (insideTravelPeriod !== null && !insideTravelPeriod) ||
      (blackoutOverlap !== null && blackoutOverlap);
    const status: ChecklistStatus = needsReview ? 'needsApproval' : 'approved';

    return {
      id: String(surcharge.id),
      name: surcharge.name,
      fromDate: surcharge.start_date,
      toDate: surcharge.end_date,
      convertedCost: isFiniteNumber(surcharge.converted_cost) ? surcharge.converted_cost : undefined,
      surchargePrice: isFiniteNumber(surcharge.sell_price) ? surcharge.sell_price : undefined,
      currencyCode: surcharge.currency_code || 'AUD',
      accumulate: surcharge.accumulate,
      insideTravelPeriod,
      blackoutOverlap,
      dealOptionNames,
      needsReview,
      status,
      statusLabel: getStatusLabel(status),
    };
  });
}

function buildCityTaxStatus({
  cityOtherTax,
  cityTaxComments,
  opportunityDataAvailable,
}: {
  cityOtherTax?: boolean;
  cityTaxComments?: string;
  opportunityDataAvailable: boolean;
}): ChecklistStatus {
  if (!opportunityDataAvailable) {
    return 'blocked';
  }
  if (!cityOtherTax) {
    return 'approved';
  }
  return cityTaxComments?.trim() ? 'approved' : 'needsApproval';
}

function buildPerRoomTaxStatus({
  cityOtherTax,
  hasPropertyTaxesPayableAtProperty,
  opportunityDataAvailable,
  propertyDataAvailable,
}: {
  cityOtherTax?: boolean;
  hasPropertyTaxesPayableAtProperty: boolean;
  opportunityDataAvailable: boolean;
  propertyDataAvailable: boolean;
}): ChecklistStatus {
  if (!opportunityDataAvailable || !propertyDataAvailable) {
    return 'blocked';
  }
  if (!cityOtherTax) {
    return 'approved';
  }
  return hasPropertyTaxesPayableAtProperty ? 'approved' : 'needsApproval';
}

function buildPaymentMechanismStatus({
  opportunityDataAvailable,
  paymentMethod,
  paymentMilestone,
}: {
  opportunityDataAvailable: boolean;
  paymentMethod?: string;
  paymentMilestone?: string;
}): ChecklistStatus {
  if (!opportunityDataAvailable) {
    return 'blocked';
  }
  if (!paymentMethod?.trim() || !paymentMilestone?.trim()) {
    return 'needsApproval';
  }
  const milestoneTotal = sumMilestonePercentages(paymentMilestone);
  return milestoneTotal !== undefined && Math.round(milestoneTotal) === 100 ? 'approved' : 'needsApproval';
}

function buildPrizeStatus({
  opportunityDataAvailable,
  prizeOrderId,
}: {
  opportunityDataAvailable: boolean;
  prizeOrderId?: string;
}): ChecklistStatus {
  if (!opportunityDataAvailable) {
    return 'blocked';
  }
  return prizeOrderId?.trim() ? 'needsApproval' : 'approved';
}

function sumMilestonePercentages(milestone: string): number | undefined {
  const matches = [...milestone.matchAll(/(\d+(?:\.\d+)?)\s*%/g)];
  if (matches.length === 0) {
    return undefined;
  }
  return matches.reduce((sum, milestoneMatch) => sum + parseFloat(milestoneMatch[1]), 0);
}

function buildTravelPeriodData(
  travelFromDate: string,
  travelToDate: string,
  surcharges: Array<SalesforceSurchargePeriod>,
) {
  const travelDays = daysBetween(travelFromDate, travelToDate);
  const clampedRanges = surcharges
    .map((surcharge) => ({
      start: surcharge.start_date < travelFromDate ? travelFromDate : surcharge.start_date,
      end: surcharge.end_date > travelToDate ? travelToDate : surcharge.end_date,
    }))
    .filter((range) => range.start <= range.end);
  const surchargeDays = countUnionDays(clampedRanges);
  return { travelDays, surchargeDays };
}

function surchargesOverlapDates(start1: string, end1: string, start2: string, end2: string): boolean {
  return rangeOverlap(start1, toExclusiveEnd(end1), start2, toExclusiveEnd(end2));
}

function daysBetween(from: string, to: string): number {
  const fromMs = new Date(from).getTime();
  const toMs = new Date(to).getTime();
  return Math.max(0, Math.round((toMs - fromMs) / (1000 * 60 * 60 * 24)) + 1);
}

function countUnionDays(ranges: Array<{ start: string; end: string }>): number {
  if (ranges.length === 0) {
    return 0;
  }

  const sorted = [...ranges].sort((a, b) => (a.start < b.start ? -1 : 1));
  let totalDays = 0;
  let currentStart = sorted[0].start;
  let currentEnd = sorted[0].end;

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].start <= currentEnd) {
      if (sorted[i].end > currentEnd) {
        currentEnd = sorted[i].end;
      }
    } else {
      totalDays += daysBetween(currentStart, currentEnd);
      currentStart = sorted[i].start;
      currentEnd = sorted[i].end;
    }
  }

  return totalDays + daysBetween(currentStart, currentEnd);
}

export function buildBundledExperienceRows({
  dealCurrency,
  hasOpportunitySourceError,
  hotelBlackoutDates,
  hotelTravelFrom,
  hotelTravelTo,
  relatedDeals,
}: {
  dealCurrency?: string;
  hasOpportunitySourceError?: boolean;
  hotelBlackoutDates?: Array<{ from__c?: string; name?: string; to__c?: string }>;
  hotelTravelFrom?: string;
  hotelTravelTo?: string;
  relatedDeals?: Array<OpportunityRelatedDeal>;
}): Array<BundledExperienceChecklistRow> {
  const bundledDeals = (relatedDeals ?? []).filter(isBundledExperienceDeal);
  const groupedDeals = new Map<string, Array<OpportunityRelatedDeal>>();

  for (const deal of bundledDeals) {
    const key = buildBundledExperienceKey(deal);
    groupedDeals.set(key, [...(groupedDeals.get(key) ?? []), deal]);
  }

  return Array.from(groupedDeals.entries()).map(([key, deals]) => {
    const sourceDeal = deals[0];
    const bundledExperience = normalizeString(sourceDeal.bundled_experiences__c) ?? sourceDeal.name ?? key;
    const linkedDealOptions = deals.map((deal) => normalizeString(deal.name) ?? deal.id);
    const currency = normalizeString(sourceDeal.currencyisocode);
    const rowDealCurrency = dealCurrency ?? currency;
    const blackoutDateRange = normalizeString(sourceDeal.blackout_dates__c);
    const childAge =
      normalizeString(sourceDeal.child_age_permitted__c) ?? normalizeString(sourceDeal.channel_fare_type__c);
    const validityOk = isBundledValidityOk({
      hotelTravelFrom,
      hotelTravelTo,
      travelFrom: sourceDeal.travel_start_date__c,
      travelTo: sourceDeal.travel_end_date__c,
    });
    const blackoutsOk =
      !hasOpportunitySourceError &&
      isBundledBlackoutOk({
        blackoutDateRange,
        hotelBlackoutDates,
      });
    const currencyOk = !!currency && !!rowDealCurrency && currency === rowDealCurrency;
    const childAgeOk = isBundledChildAgeOk(childAge);
    const status: ChecklistStatus =
      validityOk && blackoutsOk && currencyOk && childAgeOk ? 'approved' : 'requires approval';

    return {
      blackoutDateRange,
      blackoutsOk,
      bundledExperience,
      childAge,
      childAgeOk,
      currency,
      currencyOk,
      dealCurrency: rowDealCurrency,
      field: BUNDLED_EXPERIENCES_FIELD,
      id: key,
      linkedDealOptions,
      status,
      statusLabel: getStatusLabel(status),
      travelFrom: sourceDeal.travel_start_date__c ?? undefined,
      travelTo: sourceDeal.travel_end_date__c ?? undefined,
      validityOk,
    };
  });
}

export function buildDatesRestrictionsRows({
  campaignType,
  opportunityDeals,
  packages,
}: {
  campaignType?: string;
  opportunityDeals?: Array<OpportunityDeal>;
  packages?: Array<App.Package>;
}): Array<DatesRestrictionsChecklistRow> {
  const packageRows = packages ?? [];
  const opportunityDealsByPackageId = mapOpportunityDealsByPackageId(opportunityDeals);
  const flexiPackages = packageRows.filter((offerPackage) => !!(offerPackage as unknown as RecordLike).flexible_nights);

  return [
    buildFlexiDealOptionsRow({ campaignType, flexiPackages }),
    buildOfferDiscountsRow(packageRows),
    buildFlexiNightMarginRow({ flexiPackages, opportunityDealsByPackageId }),
  ];
}

export function getStatusLabel(status: ChecklistStatus) {
  switch (status) {
    case 'approved':
      return 'Approved';
    case 'needsApproval':
      return 'CM approval required';
    case 'requires approval':
      return 'Requires approval';
    case 'blocked':
      return 'Blocked';
    default:
      return 'Unknown';
  }
}

export function formatOptionalNumber(value?: number) {
  return isFiniteNumber(value) ? String(value) : 'N/A';
}

export function formatMoney(value?: number, currencyCode = 'AUD') {
  return isFiniteNumber(value) ? `${currencyCode} ${value}` : 'N/A';
}

export function formatContextDate(value?: string) {
  return value ? formatDateShortDD(value) : 'Not available';
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function normalizeString(value?: string | null) {
  return value?.trim() || undefined;
}

function isBundledExperienceDeal(deal: OpportunityRelatedDeal) {
  return (
    !!normalizeString(deal.bundled_experiences__c) || isPositiveNumber(Number(deal.number_of_bundled_experience__c))
  );
}

function buildBundledExperienceKey(deal: OpportunityRelatedDeal) {
  return [
    normalizeString(deal.bundled_experiences__c) ?? deal.id,
    normalizeString(deal.travel_start_date__c) ?? '',
    normalizeString(deal.travel_end_date__c) ?? '',
    normalizeString(deal.blackout_dates__c) ?? '',
    normalizeString(deal.currencyisocode) ?? '',
    normalizeString(deal.child_age_permitted__c) ?? normalizeString(deal.channel_fare_type__c) ?? '',
  ]
    .join(':')
    .toLowerCase();
}

export function isBundledValidityOk({
  hotelTravelFrom,
  hotelTravelTo,
  travelFrom,
  travelTo,
}: {
  hotelTravelFrom?: string;
  hotelTravelTo?: string;
  travelFrom?: string | null;
  travelTo?: string | null;
}) {
  const bundledFrom = parseChecklistDate(travelFrom);
  const bundledTo = parseChecklistDate(travelTo);
  const hotelFrom = parseChecklistDate(hotelTravelFrom);
  const hotelTo = parseChecklistDate(hotelTravelTo);

  if (!bundledFrom?.isValid() || !bundledTo?.isValid() || !hotelTo?.isValid()) {
    return false;
  }

  const startsInsideTravel = hotelFrom?.isValid() ? !bundledFrom.isBefore(hotelFrom, 'day') : true;
  return startsInsideTravel && !bundledTo.isAfter(hotelTo, 'day');
}

export function isBundledBlackoutOk({
  blackoutDateRange,
  hotelBlackoutDates,
}: {
  blackoutDateRange?: string;
  hotelBlackoutDates?: Array<{ from__c?: string; name?: string; to__c?: string }>;
}) {
  if (!blackoutDateRange || blackoutDateRange.toLowerCase() === 'none') {
    return true;
  }

  const normalizedBundledBlackoutEntries = normalizeBlackoutEntries(blackoutDateRange);
  const normalizedHotelBlackoutEntries = (hotelBlackoutDates ?? []).map((blackout) =>
    normalizeComparisonText([blackout.name, blackout.from__c, blackout.to__c].filter(Boolean).join(' ')),
  );

  return (
    normalizedBundledBlackoutEntries.length > 0 &&
    normalizedHotelBlackoutEntries.length > 0 &&
    normalizedBundledBlackoutEntries.every((bundledBlackout) =>
      normalizedHotelBlackoutEntries.some((hotelBlackout) => hotelBlackout.includes(bundledBlackout)),
    )
  );
}

function normalizeBlackoutEntries(value: string) {
  return value
    .split(';')
    .map((part) => normalizeComparisonText(part))
    .filter(Boolean);
}

export function isBundledChildAgeOk(childAge?: string) {
  if (!childAge || !/\b(child|children|kid|kids)\b/i.test(childAge)) {
    return true;
  }

  return false;
}

function normalizeComparisonText(value: string) {
  return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

function parseChecklistDate(value?: string | null) {
  return value ? parseDayjs(value) : undefined;
}

function findPolicyBibleCampaignRule(setupInput?: SetupRuleInput) {
  const chainName = setupInput?.chainName ?? setupInput?.account?.contractTemplateId;
  const geo = setupInput?.opportunity?.geo ?? setupInput?.property?.country;
  const normalizedChainName = normalizePolicyBibleKey(chainName);
  const normalizedGeo = normalizePolicyBibleGeo(geo);

  if (!normalizedChainName || !normalizedGeo) {
    return undefined;
  }

  return POLICY_BIBLE_CAMPAIGN_RULES.find(
    (rule) =>
      normalizePolicyBibleKey(rule.chain) === normalizedChainName && policyBibleGeoMatches(rule.geo, normalizedGeo),
  );
}

function policyBibleGeoMatches(ruleGeo: string, loadedGeo: string) {
  const normalizedRuleGeo = normalizePolicyBibleGeo(ruleGeo);

  if (!normalizedRuleGeo) {
    return false;
  }

  if (normalizedRuleGeo === loadedGeo) {
    return true;
  }

  return normalizedRuleGeo
    .split('/')
    .map((geoPart) => normalizePolicyBibleGeo(geoPart))
    .includes(loadedGeo);
}

function normalizePolicyBibleKey(value?: string | null) {
  return normalizeString(value)?.toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ');
}

function normalizePolicyBibleGeo(value?: string | null) {
  const normalizedValue = normalizePolicyBibleKey(value);

  switch (normalizedValue) {
    case 'au':
    case 'aus':
    case 'australia':
      return 'australia';
    case 'intl':
    case 'international':
      return 'intl';
    case 'intl/aus':
    case 'international/australia':
      return 'intl/australia';
    default:
      return normalizedValue;
  }
}

function buildCampaignRow({
  action,
  approvalNote,
  approved,
  dataPoints,
  evidence,
  field,
  id,
  packageName,
  rule,
  source,
  summary,
  value,
}: Omit<CampaignChecklistRow, 'status' | 'statusLabel' | 'dataPoints' | 'summary'> & {
  approved: boolean;
  dataPoints?: Array<CampaignChecklistDataPoint>;
  summary?: string;
}): CampaignChecklistRow {
  const status: ChecklistStatus = approved ? 'approved' : 'requires approval';

  return {
    action,
    approvalNote,
    dataPoints: dataPoints ?? [{ label: 'Loaded value', value }],
    evidence,
    field,
    id,
    packageName,
    rule,
    source,
    status,
    statusLabel: getStatusLabel(status),
    summary: summary ?? getCampaignSummary(field, approved),
    value,
  };
}

function getCampaignSummary(field: CampaignChecklistRow['field'], approved: boolean) {
  switch (field) {
    case 'Date change allowed':
      return approved
        ? 'Date change allowance matches the policy bible.'
        : 'Date change allowance needs policy bible review.';
    case 'Days prior to check-in for date changes':
      return approved
        ? 'Days prior to check-in for date changes matches policy bible.'
        : 'Days prior to check-in for date changes needs policy bible review.';
    case 'Number of date changes':
      return approved
        ? 'Number of permitted date changes matches the policy bible.'
        : 'Number of permitted date changes needs policy bible review.';
    case 'Booking data cut-off':
      return approved
        ? 'Booking data cut-off is populated.'
        : 'Booking data cut-off is blank and should be populated to 2.';
    case 'Package upgrades allowed':
      return approved ? 'Package upgrades are allowed.' : 'Package upgrades need review.';
    case 'SEM support':
      return approved ? 'SEM support is not enabled.' : 'SEM support is enabled.';
    case 'Campaign type':
      return approved ? 'Campaign type is a supported Flash type.' : 'Campaign type needs review.';
    case 'Contract Terms Modification':
      return approved
        ? 'Contract terms modification field is blank.'
        : 'Contract terms modification field is populated.';
    case 'Payment Terms Modification':
      return approved ? 'Payment Terms Modification is blank.' : 'Payment Terms Modification is enabled.';
    case 'Book By date':
      return approved ? 'Book By is approximately one month before Travel To.' : 'Book By date needs review.';
    case 'Suggested value':
      return approved
        ? 'Suggested value equals value in cost currency for every deal option.'
        : 'Suggested value needs review.';
    default:
      return approved ? 'Campaign row is approved.' : 'Campaign row needs review.';
  }
}

function buildFlexiDealOptionsRow({
  campaignType,
  flexiPackages,
}: {
  campaignType?: string;
  flexiPackages: Array<App.Package>;
}): DatesRestrictionsChecklistRow {
  const normalizedCampaignType = campaignType?.trim();
  const isFlexiCampaign = normalizedCampaignType ? /flexi/i.test(normalizedCampaignType) : undefined;
  const flexiPackageCount = flexiPackages.length;
  const hasExpectedFlexiPackages =
    typeof isFlexiCampaign === 'boolean'
      ? isFlexiCampaign
        ? flexiPackageCount === 1
        : flexiPackageCount === 0
      : false;
  const status: ChecklistStatus = hasExpectedFlexiPackages ? 'approved' : 'requires approval';

  return {
    details: [
      { label: 'Campaign type', value: normalizedCampaignType || 'Not available' },
      { label: 'Flexi deal options', value: String(flexiPackageCount) },
      {
        label: 'Flexi option names',
        value: flexiPackages.map(getPackageDisplayName).join(', ') || 'None',
      },
    ],
    field: DATES_RESTRICTIONS_FIELDS.flexiDealOptions,
    id: 'dates-restrictions:flexi-deal-options',
    rule: 'If flexi night, need 1 deal option with flexi night. If standard, no deal options with flexi night.',
    sourceValue:
      typeof isFlexiCampaign === 'boolean'
        ? `${normalizedCampaignType}; ${flexiPackageCount} flexi deal option${flexiPackageCount === 1 ? '' : 's'}`
        : `Campaign type unavailable; ${flexiPackageCount} flexi deal option${flexiPackageCount === 1 ? '' : 's'}`,
    status,
    statusLabel: getStatusLabel(status),
    summary: hasExpectedFlexiPackages
      ? 'Flexi-night setup aligns with loaded deal options.'
      : 'Flexi-night setup needs review.',
    title: DATES_RESTRICTIONS_FIELDS.flexiDealOptions,
  };
}

function buildOfferDiscountsRow(packages: Array<App.Package>): DatesRestrictionsChecklistRow {
  const discountDetails = packages.map((offerPackage) => {
    const discount = readNumber(offerPackage as unknown as RecordLike, ['discount_percent']);

    return {
      discount,
      name: getPackageDisplayName(offerPackage),
    };
  });
  const hasPackages = discountDetails.length > 0;
  const hasAllDiscounts = discountDetails.every(({ discount }) => isFiniteNumber(discount));
  const hasOutOfRangeDiscount = discountDetails.some(
    ({ discount }) => isFiniteNumber(discount) && (discount < 15 || discount > 75),
  );
  const status: ChecklistStatus =
    hasPackages && hasAllDiscounts && !hasOutOfRangeDiscount ? 'approved' : 'requires approval';

  return {
    details: discountDetails.map(({ discount, name }) => ({
      label: name,
      value: isFiniteNumber(discount) ? `${discount}%` : 'Not available',
    })),
    field: DATES_RESTRICTIONS_FIELDS.offerDiscounts,
    id: 'dates-restrictions:offer-discounts',
    rule: 'If any offer discount is greater than 75% or less than 15%, flag for approval; otherwise auto-approve.',
    sourceValue: hasPackages
      ? discountDetails
          .map(({ discount, name }) => `${name}: ${isFiniteNumber(discount) ? `${discount}%` : 'N/A'}`)
          .join('; ')
      : 'No deal options available',
    status,
    statusLabel: getStatusLabel(status),
    summary:
      hasPackages && hasAllDiscounts && !hasOutOfRangeDiscount
        ? 'Offer discounts are within the approved range.'
        : 'Offer discounts need review.',
    title: DATES_RESTRICTIONS_FIELDS.offerDiscounts,
  };
}

function buildFlexiNightMarginRow({
  flexiPackages,
  opportunityDealsByPackageId,
}: {
  flexiPackages: Array<App.Package>;
  opportunityDealsByPackageId: Map<string, OpportunityDeal>;
}): DatesRestrictionsChecklistRow {
  const marginDetails = flexiPackages.map((offerPackage) => {
    const packageRecord = offerPackage as unknown as RecordLike;
    const packageId = readString(packageRecord, ['id_salesforce_external']);
    const opportunityDeal = packageId ? opportunityDealsByPackageId.get(packageId) : undefined;
    const margin = isFiniteNumber(opportunityDeal?.extra_night_margin__c)
      ? opportunityDeal.extra_night_margin__c
      : readNumber(packageRecord, EXTRA_NIGHT_MARGIN_FIELDS);

    return {
      margin,
      name: getPackageDisplayName(offerPackage),
    };
  });
  const hasFlexiPackages = marginDetails.length > 0;
  const hasAllMargins = marginDetails.every(({ margin }) => isFiniteNumber(margin));
  const hasOutOfRangeMargin = marginDetails.some(
    ({ margin }) => isFiniteNumber(margin) && (margin < 10 || margin > 50),
  );
  const status: ChecklistStatus =
    !hasFlexiPackages || (hasAllMargins && !hasOutOfRangeMargin) ? 'approved' : 'requires approval';

  return {
    details: hasFlexiPackages
      ? marginDetails.map(({ margin, name }) => ({
          label: name,
          value: isFiniteNumber(margin) ? `${margin}%` : 'Not available',
        }))
      : [{ label: 'Flexi deal options', value: 'None' }],
    field: DATES_RESTRICTIONS_FIELDS.flexiNightMargin,
    id: 'dates-restrictions:flexi-night-margin',
    rule: 'If any flexi night margin is below 10% or above 50%, flag for approval; otherwise auto-approve.',
    sourceValue: hasFlexiPackages
      ? marginDetails.map(({ margin, name }) => `${name}: ${isFiniteNumber(margin) ? `${margin}%` : 'N/A'}`).join('; ')
      : 'No flexi-night deal options',
    status,
    statusLabel: getStatusLabel(status),
    summary:
      !hasFlexiPackages || (hasAllMargins && !hasOutOfRangeMargin)
        ? 'Flexi night margins are within approved range.'
        : 'Flexi night margins need review.',
    title: DATES_RESTRICTIONS_FIELDS.flexiNightMargin,
  };
}

function getPackageDisplayName(offerPackage: App.Package) {
  return (
    offerPackage.deal_option_name || offerPackage.name || String(offerPackage.id_salesforce_external || 'Deal option')
  );
}

function mapOpportunityDealsByPackageId(opportunityDeals?: Array<OpportunityDeal>) {
  return new Map(
    (opportunityDeals ?? [])
      .map((deal) => {
        const dealRecord = deal as unknown as RecordLike;
        const dealId = readString(dealRecord, ['id', 'sfid']);

        return dealId ? ([dealId, deal] as const) : undefined;
      })
      .filter((dealEntry): dealEntry is readonly [string, OpportunityDeal] => !!dealEntry),
  );
}

function buildPolicyBibleNumberRow({
  field,
  id,
  loadedValue,
  policyBibleRule,
  source,
  value,
}: Pick<CampaignChecklistRow, 'field' | 'id' | 'source' | 'value'> & {
  loadedValue?: number;
  policyBibleRule?: PolicyBibleCampaignRule;
}): CampaignChecklistRow {
  const expectedValue = policyBibleRule?.dateChangeAllowedDays;
  const approved = isFiniteNumber(loadedValue) && isFiniteNumber(expectedValue) && loadedValue === expectedValue;

  return buildCampaignRow({
    approved,
    dataPoints: [
      { label: 'Loaded value', value },
      { label: 'Expected value', value: formatOptionalNumber(expectedValue) },
    ],
    evidence: getPolicyBibleEvidence({ approved, expectedValue, loadedValue, policyBibleRule }),
    field,
    id,
    rule: 'Loaded value must match the Policy Bible.',
    source,
    value,
  });
}

function buildPolicyBibleTextRow({
  field,
  id,
  loadedValue,
  policyBibleRule,
  source,
  value,
}: Pick<CampaignChecklistRow, 'field' | 'id' | 'source' | 'value'> & {
  loadedValue?: string | null;
  policyBibleRule?: PolicyBibleCampaignRule;
}): CampaignChecklistRow {
  const expectedValue = policyBibleRule?.numberOfDateChanges;
  const approved =
    !!normalizeString(loadedValue) &&
    !!normalizeString(expectedValue) &&
    normalizeComparable(loadedValue) === normalizeComparable(expectedValue);

  return buildCampaignRow({
    approved,
    dataPoints: [
      { label: 'Loaded value', value },
      { label: 'Expected value', value: formatStringValue(expectedValue) },
    ],
    evidence: getPolicyBibleEvidence({ approved, expectedValue, loadedValue, policyBibleRule }),
    field,
    id,
    rule: 'Loaded value must match the Policy Bible.',
    source,
    value,
  });
}

function getPolicyBibleEvidence({
  approved,
  expectedValue,
  loadedValue,
  policyBibleRule,
}: {
  approved: boolean;
  expectedValue?: number | string;
  loadedValue?: number | string | null;
  policyBibleRule?: PolicyBibleCampaignRule;
}) {
  if (!policyBibleRule) {
    return 'Policy Bible expected value is not available for the loaded chain and geo.';
  }

  if (expectedValue == null || expectedValue === '') {
    return 'Policy Bible expected value is blank or ambiguous for the loaded chain and geo.';
  }

  if (loadedValue == null || loadedValue === '') {
    return 'Loaded value is missing.';
  }

  return approved ? 'Loaded value matches the Policy Bible.' : 'Loaded value does not match the Policy Bible.';
}

function buildBookByRow({
  bookByDate,
  travelToDate,
}: {
  bookByDate?: string | null;
  travelToDate?: string | null;
}): CampaignChecklistRow {
  const daysBeforeTravelTo = getDaysBetweenDates(bookByDate, travelToDate);
  const approved =
    daysBeforeTravelTo != null &&
    daysBeforeTravelTo >= MIN_BOOK_BY_DAYS_BEFORE_TRAVEL_TO &&
    daysBeforeTravelTo <= MAX_BOOK_BY_DAYS_BEFORE_TRAVEL_TO;

  return buildCampaignRow({
    approved,
    dataPoints: [
      { label: 'Book By date', value: formatStringValue(bookByDate) },
      { label: 'Travel To date', value: formatStringValue(travelToDate) },
      { label: 'Expected window', value: '25 to 62 days before Travel To' },
    ],
    evidence:
      daysBeforeTravelTo == null
        ? 'Book By or Travel To date is missing or invalid.'
        : `Book By is ${daysBeforeTravelTo} days before Travel To.`,
    field: 'Book By date',
    id: 'campaign-book-by-date',
    rule: 'Auto-approve when Book By is 25 days to 2 months before Travel To.',
    source: 'Opportunity key dates / offer book_by_date and travel_to_date',
    value: `${formatStringValue(bookByDate)} / ${formatStringValue(travelToDate)}`,
  });
}

function buildSuggestedValueRows(
  packages?: Array<App.Package>,
  opportunityDeals: Array<OpportunityDeal> = [],
): Array<CampaignChecklistRow> {
  const offerPackages = packages?.length ? packages : [undefined];
  const opportunityDealsById = new Map(opportunityDeals.map((deal) => [String(deal.id), deal]));

  return offerPackages.map((offerPackage, index) => {
    const packageRecord = (offerPackage ?? {}) as unknown as RecordLike;
    const opportunityDeal = offerPackage?.id_salesforce_external
      ? opportunityDealsById.get(String(offerPackage.id_salesforce_external))
      : undefined;
    const dealRecord = (opportunityDeal ?? {}) as unknown as RecordLike;
    const dealCurrencyCode = readString(dealRecord, ['currencyisocode']);
    const priceValue = getPackagePriceValue(packageRecord, dealCurrencyCode);
    const value = readNumber(packageRecord, ['value']) ?? priceValue?.value;
    const suggestedValue =
      readNumber(packageRecord, ['suggested_value__c']) ?? readNumber(dealRecord, ['suggested_value__c']);
    const currencyCode = dealCurrencyCode ?? priceValue?.currencyCode;
    const approved = isFiniteNumber(suggestedValue) && isFiniteNumber(value) && suggestedValue === value;
    const packageName =
      offerPackage?.deal_option_name ||
      offerPackage?.name ||
      opportunityDeal?.name ||
      (offerPackage ? `Deal option ${index + 1}` : undefined);

    return buildCampaignRow({
      approved,
      dataPoints: [
        { label: 'Suggested value', value: formatOptionalCurrency(suggestedValue, currencyCode) },
        { label: 'Value', value: formatOptionalCurrency(value, currencyCode) },
      ],
      evidence: getSuggestedValueEvidence({ suggestedValue, value }),
      field: 'Suggested value',
      id: `campaign-suggested-value-${String(offerPackage?.id_salesforce_external ?? index)}`,
      packageName,
      rule: 'Compare Suggested value with Value in cost currency.',
      source: 'Opportunity deal suggested_value__c / offer package price value',
      value: `Suggested value: ${formatOptionalCurrency(suggestedValue, currencyCode)} / Value: ${formatOptionalCurrency(
        value,
        currencyCode,
      )}`,
    });
  });
}

function getSuggestedValueEvidence({ suggestedValue, value }: { suggestedValue?: number; value?: number }) {
  if (!isFiniteNumber(suggestedValue)) {
    return 'Suggested value is missing from opportunity deal data.';
  }

  if (!isFiniteNumber(value)) {
    return 'Value is missing from package price data.';
  }

  return suggestedValue === value ? 'Suggested value matches Value.' : 'Suggested value does not match Value.';
}

function formatOptionalCurrency(value?: number, currencyCode?: string) {
  if (!isFiniteNumber(value)) {
    return 'N/A';
  }

  return currencyCode ? formatMoney(value, currencyCode) : String(value);
}

function getPackagePriceValue(packageRecord: RecordLike, currencyCode?: string) {
  const prices = packageRecord.prices;

  if (!Array.isArray(prices)) {
    return undefined;
  }

  const preferredPrice = currencyCode
    ? prices.find((price) => readString(price as RecordLike, ['currency_code']) === currencyCode)
    : undefined;
  const sortedPrices = preferredPrice
    ? [preferredPrice, ...prices.filter((price) => price !== preferredPrice)]
    : prices;

  for (const price of sortedPrices) {
    const priceRecord = price as RecordLike;
    const value = readNumber(priceRecord, ['value']);

    if (isFiniteNumber(value)) {
      return {
        currencyCode: readString(priceRecord, ['currency_code']),
        value,
      };
    }
  }

  return undefined;
}

function formatBooleanValue(value?: boolean | null) {
  if (value == null) {
    return 'Not available';
  }

  return value ? 'Yes' : 'No';
}

function formatStringValue(value?: string | number | boolean | null) {
  if (value == null || value === '') {
    return 'Not available';
  }

  return String(value);
}

function deriveSemSupport(noSemSupport?: boolean | null) {
  if (noSemSupport === false) {
    return 'Yes';
  }

  return 'No';
}

function normalizeComparable(value?: string | null) {
  return value?.trim().toLowerCase() || '';
}

function getDaysBetweenDates(from?: string | null, to?: string | null) {
  const fromDate = parseDate(from);
  const toDate = parseDate(to);

  if (!fromDate || !toDate) {
    return undefined;
  }

  const millisecondsPerDay = 24 * 60 * 60 * 1000;

  return Math.round((toDate.getTime() - fromDate.getTime()) / millisecondsPerDay);
}

function parseDate(value?: string | null) {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? undefined : date;
}

type DateRange = {
  end: number;
  start: number;
};

function parseDateRange(from?: string, to?: string): DateRange | undefined {
  const start = parseDateToUtcDay(from);
  const end = parseDateToUtcDay(to);

  if (!isFiniteNumber(start) || !isFiniteNumber(end) || end < start) {
    return undefined;
  }

  return { end, start };
}

function parseDateToUtcDay(value?: string) {
  if (!value) {
    return undefined;
  }

  const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
}

function rangeOverlapsFestiveDates(range: DateRange) {
  const startYear = new Date(range.start).getUTCFullYear();
  const endYear = new Date(range.end).getUTCFullYear();

  for (let year = startYear; year <= endYear; year += 1) {
    const festiveDays = [Date.UTC(year, 11, 24), Date.UTC(year, 11, 25), Date.UTC(year, 11, 31)];
    if (festiveDays.some((day) => day >= range.start && day <= range.end)) {
      return true;
    }
  }

  return false;
}

function mergeDateRanges(ranges: Array<DateRange>) {
  return ranges.reduce<Array<DateRange>>((merged, range) => {
    const previousRange = merged[merged.length - 1];

    if (!previousRange || range.start > previousRange.end + DAY_MS) {
      merged.push({ ...range });
      return merged;
    }

    previousRange.end = Math.max(previousRange.end, range.end);
    return merged;
  }, []);
}

function inclusiveDays(range: DateRange) {
  return Math.round((range.end - range.start) / DAY_MS) + 1;
}

function getMaxRoomCapacity(roomType?: App.RoomType) {
  if (!roomType) {
    return undefined;
  }

  const roomTypeRecord = roomType as unknown as RecordLike;
  const capacityFromField = readNumber(roomTypeRecord, MAX_ROOM_CAPACITY_FIELDS);

  if (isFiniteNumber(capacityFromField)) {
    return capacityFromField;
  }

  const capacities = roomTypeRecord.capacities;
  if (!Array.isArray(capacities)) {
    return undefined;
  }

  return getMaxDefinedNumber(capacities.map(getCapacityTotal));
}

function findRoomRate(roomType?: App.RoomType, roomRateId?: string) {
  if (!roomType || !roomRateId || !Array.isArray(roomType.room_rates)) {
    return undefined;
  }

  return roomType.room_rates.find((roomRate) => String(roomRate.id) === roomRateId);
}

function readIncludedGuestsCapacity(roomRate?: App.RoomRate) {
  const includedGuests = (roomRate as unknown as RecordLike | undefined)?.included_guests;

  if (!Array.isArray(includedGuests)) {
    return undefined;
  }

  return getMaxDefinedNumber(includedGuests.map(getCapacityTotal));
}

function getCapacityTotal(value: unknown) {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }

  const record = value as RecordLike;
  const explicitTotal = readNumber(record, ['total', 'total_guests', 'max_guests']);

  if (isFiniteNumber(explicitTotal)) {
    return explicitTotal;
  }

  const adults = readNumber(record, ['adults']) ?? 0;
  const children = readNumber(record, ['children']) ?? 0;
  const teenagers = readNumber(record, ['teenagers']) ?? 0;

  return adults + children + teenagers;
}

function getMaxDefinedNumber(values: Array<number | undefined>) {
  const numericValues = values.filter(isFiniteNumber);

  return numericValues.length > 0 ? Math.max(...numericValues) : undefined;
}

function readString(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const fieldValue = record[field];

    if (typeof fieldValue === 'string' && fieldValue.trim()) {
      return fieldValue;
    }
  }

  return undefined;
}

function readNumber(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const fieldValue = record[field];
    const numberValue = typeof fieldValue === 'string' && fieldValue.trim() !== '' ? Number(fieldValue) : fieldValue;

    if (isFiniteNumber(numberValue)) {
      return numberValue;
    }
  }

  return undefined;
}

function readBoolean(record: RecordLike, fields: Array<string>) {
  for (const field of fields) {
    const value = record[field];

    if (typeof value === 'boolean') {
      return value;
    }
  }

  return undefined;
}

function isPositiveNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}
